<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanAdvancedFiltersDrawer from 'dashboard/components-next/Kanban/KanbanAdvancedFiltersDrawer.vue';

const emit = defineEmits(['search', 'clear']);

const agent = defineModel('agent', { type: String, default: '' });
const verificationStatus = defineModel('verificationStatus', {
  type: String,
  default: '',
});
const priority = defineModel('priority', { type: String, default: '' });
const channel = defineModel('channel', { type: String, default: '' });
const company = defineModel('company', { type: String, default: '' });
const sortBy = defineModel('sortBy', { type: String, required: true });

const { t } = useI18n();

const isDrawerOpen = ref(false);
const searchValue = ref('');

const agents = useMapGetter('agents/getAgents');

const agentOptions = computed(() => [
  { label: t('KANBAN.FILTERS.ALL'), value: '' },
  ...agents.value.map(a => ({ label: a.name, value: String(a.id) })),
]);

const advancedFilterCount = computed(
  () =>
    [
      company.value,
      priority.value,
      channel.value,
      verificationStatus.value,
    ].filter(Boolean).length
);

const hasActiveFilters = computed(
  () =>
    advancedFilterCount.value > 0 ||
    Boolean(agent.value) ||
    Boolean(searchValue.value)
);

const handleSearchInput = () => {
  emit('search', searchValue.value);
};

const clearAll = () => {
  agent.value = '';
  verificationStatus.value = '';
  priority.value = '';
  channel.value = '';
  company.value = '';
  searchValue.value = '';
  emit('search', '');
  emit('clear');
};
</script>

<template>
  <div
    class="flex items-center flex-shrink-0 gap-2 px-1 pb-2 overflow-x-auto flex-nowrap"
  >
    <Input
      v-model="searchValue"
      :placeholder="t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
      custom-input-class="h-8 !pt-1 !pb-1"
      class="!w-48 shrink-0"
      @input="handleSearchInput"
    />
    <ComboBox
      v-model="agent"
      :options="agentOptions"
      :placeholder="t('KANBAN.FILTERS.AGENT')"
      class="!w-40 shrink-0 [&>div>button]:h-8"
    />
    <button
      type="button"
      disabled
      class="flex items-center h-8 gap-1.5 px-3 text-sm border rounded-lg cursor-not-allowed shrink-0 border-n-weak bg-n-alpha-1 text-n-slate-9"
    >
      <span class="i-lucide-folder size-3.5" />
      {{ t('KANBAN.FILTERS.PROJECT') }}
      <span
        class="px-1.5 py-0.5 text-[9px] font-bold tracking-wide rounded text-n-amber-11 bg-n-amber-3"
      >
        {{ t('KANBAN.FILTERS.SOON') }}
      </span>
    </button>

    <div class="relative shrink-0">
      <Button
        icon="i-lucide-list-filter"
        :variant="isDrawerOpen ? 'solid' : 'faded'"
        :color="isDrawerOpen ? 'blue' : 'slate'"
        size="sm"
        @click="isDrawerOpen = true"
      />
      <span
        v-if="advancedFilterCount"
        class="absolute -top-1.5 -right-1.5 flex items-center justify-center size-4 rounded-full bg-n-ruby-9 text-white text-[10px] font-semibold leading-none"
      >
        {{ advancedFilterCount }}
      </span>
    </div>

    <Button
      v-if="hasActiveFilters"
      icon="i-lucide-rotate-ccw"
      variant="faded"
      color="slate"
      size="sm"
      class="shrink-0"
      @click="clearAll"
    />

    <KanbanAdvancedFiltersDrawer
      v-model:company="company"
      v-model:priority="priority"
      v-model:channel="channel"
      v-model:verification-status="verificationStatus"
      v-model:sort-by="sortBy"
      :open="isDrawerOpen"
      @close="isDrawerOpen = false"
    />
  </div>
</template>
